import { Dispatch, SetStateAction, useMemo } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import {
	AllAvailableItemsApi,
	CsvImportOverviewFormType,
	ExportCSVTranslationsApi,
	ImportOverviewConfig,
} from "../../../../services/types";
import { snakeToCamelCase } from "../../../../utils/componentUtils";
import { getMappingOptions } from "../../utils/utils";

interface Props {
	form: UseFormReturn<CsvImportOverviewFormType>;
	mappingGroup: string;
	mappingsAvailableItems: Record<string, AllAvailableItemsApi[]>;
	translations: ExportCSVTranslationsApi;
	importOverviewConfig: ImportOverviewConfig;
	setErrors: Dispatch<SetStateAction<CustomValidationError<CsvImportOverviewFormType>[]>>;
}

const CsvValuesMappingGroupRows = ({
	form,
	mappingGroup,
	mappingsAvailableItems,
	translations,
	importOverviewConfig,
	setErrors,
}: Props) => {
	const { t } = useTranslation("lib");
	const {
		control,
		formState: { errors },
		watch,
	} = form;
	const { fields } = useFieldArray({
		control,
		name: `mappings.${mappingGroup}`,
	});
	const { dryImportMappings } = importOverviewConfig;
	const mappingOptions = getMappingOptions(mappingsAvailableItems[mappingGroup], dryImportMappings, mappingGroup, t);
	const mappingGroupTranslation = translations.database_column_name[snakeToCamelCase(mappingGroup)];
	const requiredMappingValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	if (!mappingOptions) return null;

	return (
		<>
			<tr>
				<td colSpan={4} className="mapping-group-name py-3">
					{mappingGroupTranslation}
				</td>
			</tr>
			{fields.map((field, index) => {
				const { id, csv_value } = field;

				return (
					<tr key={id} className="fw-light">
						<td className="action" />
						<td>{csv_value}</td>
						<td>
							<FormSelectGroup
								name={`mappings.${mappingGroup}.${index}.db_id`}
								errors={errors}
								control={control}
								options={mappingOptions}
								customValidationConfig={requiredMappingValidationConfig}
							/>
						</td>
						<td className="action" />
					</tr>
				);
			})}
		</>
	);
};

export default CsvValuesMappingGroupRows;
